import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  ArrowLeft, 
  Loader2, 
  RefreshCw,
  AlertTriangle,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  supplierReportApiService, 
  SupplierPerformanceDetailsResponse,
  SupplierMonthlyRatingDto,
  SupplierPerformanceDetailDto
} from '../services/supplierReportApi';
import { useBranch } from '../contexts/BranchContext';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell
} from 'recharts';

const SupplierPerformanceDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedBranch } = useBranch();
  const [loading, setLoading] = useState(true);
  const [detailsData, setDetailsData] = useState<SupplierPerformanceDetailsResponse | null>(null);
  const [monthlyRatings, setMonthlyRatings] = useState<SupplierMonthlyRatingDto[]>([]);
  const [loadingRatings, setLoadingRatings] = useState(false);

  // Get parameters from URL
  const supplierId = searchParams.get('supplierId');
  const yearParam = searchParams.get('year');
  const monthParam = searchParams.get('month');
  
  // Month/Year selection state - default to values from URL or current month
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState<number>(
    yearParam ? parseInt(yearParam) : currentDate.getFullYear()
  );
  const [selectedMonth, setSelectedMonth] = useState<number>(
    monthParam ? parseInt(monthParam) : currentDate.getMonth() + 1
  );

  // Generate year options (last 5 years and next 1 year)
  const yearOptions = useMemo(() => {
    const currentYear = currentDate.getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 1; i++) {
      years.push(i);
    }
    return years.reverse();
  }, []);

  // Month options
  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  useEffect(() => {
    if (supplierId) {
      loadSupplierDetails();
      loadMonthlyRatings();
    } else {
      toast.error('Supplier ID is required');
      navigate('/reports/supplier-performance');
    }
  }, [supplierId, selectedBranch]);

  useEffect(() => {
    if (supplierId && selectedBranch) {
      loadSupplierDetails();
    }
  }, [selectedYear, selectedMonth]);

  const loadSupplierDetails = async () => {
    if (!supplierId || !selectedBranch) {
      return;
    }

    try {
      setLoading(true);
      
      const response = await supplierReportApiService.getSupplierPerformanceDetails(
        parseInt(supplierId),
        selectedBranch,
        selectedYear,
        selectedMonth
      );

      if (response.success && response.data) {
        setDetailsData(response.data);
      } else {
        toast.error(response.message || 'Failed to load supplier performance details');
        setDetailsData(null);
      }
    } catch (error) {
      // console.error('Error loading supplier performance details:', error);
      toast.error('Failed to load supplier performance details');
      setDetailsData(null);
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyRatings = async () => {
    if (!supplierId || !selectedBranch) {
      return;
    }

    try {
      setLoadingRatings(true);
      const response = await supplierReportApiService.getSupplierPerformanceLast12Months(
        selectedBranch,
        parseInt(supplierId, 10)
      );

      if (response.success && response.data) {
        setMonthlyRatings(response.data);
      } else {
        setMonthlyRatings([]);
      }
    } catch {
      setMonthlyRatings([]);
    } finally {
      setLoadingRatings(false);
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatNumber = (value?: number): string => {
    if (value === null || value === undefined || isNaN(value)) return '0.00';
    return value.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  // Prepare chart data for monthly ratings
  const chartData = useMemo(() => {
    if (!monthlyRatings || monthlyRatings.length === 0) return [];
    
    return monthlyRatings.map(rating => {
      const isSelected = rating.year === selectedYear && rating.month === selectedMonth;
      return {
        month: rating.monthName || `Month ${rating.month} ${rating.year}`,
        rating: rating.deliveryRating ?? 0,
        year: rating.year,
        monthNum: rating.month,
        isSelected: isSelected
      };
    });
  }, [monthlyRatings, selectedYear, selectedMonth]);
  
  const handleBarClick = (data: any) => {
    if (data && data.year && data.monthNum) {
      setSelectedYear(data.year);
      setSelectedMonth(data.monthNum);
      // Data will be reloaded via useEffect when selectedYear/selectedMonth changes
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-3 text-gray-600">Loading supplier performance details...</span>
        </div>
      </div>
    );
  }

  if (!detailsData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">No data available</p>
            <Button onClick={() => navigate('/reports/supplier-performance')} variant="outline" className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Performance Report
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/reports/supplier-performance')}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-primary-700" />
                </div>
                <div>
                  <CardTitle className="text-2xl text-gray-900">
                    {detailsData.supplierName} - Performance Details
                  </CardTitle>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  loadSupplierDetails();
                  loadMonthlyRatings();
                }}
                disabled={loading || loadingRatings}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading || loadingRatings ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Monthly Rating Chart */}
        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-gray-800">
              Delivery Rating Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingRatings ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                <span className="ml-3 text-gray-600">Loading rating data...</span>
              </div>
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData} onClick={handleBarClick}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                  />
                  <YAxis 
                    label={{ value: 'Rating', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value: number) => value.toFixed(2)}
                    labelStyle={{ color: '#374151' }}
                  />
                  <Bar 
                    dataKey="rating" 
                    style={{ cursor: 'pointer' }}
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.isSelected ? '#dc2626' : '#fca5a5'}
                        onClick={() => handleBarClick(entry)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                No rating data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* PO/GRN Details Table */}
        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-gray-800">
              Purchase Orders & GRN Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              // Filter data by selected month and year
              const filteredDetails = detailsData.poGrnDetails?.filter(detail => 
                detail.receiveYear === selectedYear && detail.receiveMonth === selectedMonth
              ) || [];

              // Transform data to have one row per GRN
              // Each detail object represents one GRN
              const grnRows = filteredDetails.map(detail => ({
                grnNumber: detail.grnNumber || 'N/A',
                poNumber: detail.poNumber || 'N/A',
                receiveDate: detail.receiveDate,
                expectedDelivery: detail.expectedDeliveryDate,
                delayDays: detail.deliveryDelayDays
              }));

              return grnRows.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-semibold">GRN Number</TableHead>
                        <TableHead className="font-semibold">PO Number</TableHead>
                        <TableHead className="font-semibold">Receive Date</TableHead>
                        <TableHead className="font-semibold">Expected Delivery</TableHead>
                        <TableHead className="font-semibold">Delay By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {grnRows.map((row, index) => (
                        <TableRow key={index} className="hover:bg-gray-50">
                          <TableCell className="font-medium">
                            {row.grnNumber}
                          </TableCell>
                          <TableCell className="font-medium">
                            {row.poNumber}
                          </TableCell>
                          <TableCell>
                            {row.receiveDate ? formatDate(row.receiveDate) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {row.expectedDelivery ? formatDate(row.expectedDelivery) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {row.delayDays !== null && row.delayDays !== undefined ? (
                              row.delayDays <= 0 ? (
                                <span className="text-green-600 font-medium">On time</span>
                              ) : (
                                <span className="text-red-600 font-medium">Delay by {row.delayDays} days</span>
                              )
                            ) : (
                              <span className="text-gray-500">N/A</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="flex items-center justify-center h-32 text-gray-500">
                  No PO/GRN details available for {monthOptions.find(m => m.value === selectedMonth)?.label} {selectedYear}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SupplierPerformanceDetailPage;

